// src/components/Template/TemplateRenameDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
} from '@mui/material';

interface TemplateRenameDialogProps {
  open: boolean;
  onClose: () => void;
  currentName: string;
  onRename: (newName: string) => Promise<void>;
}

const TemplateRenameDialog: React.FC<TemplateRenameDialogProps> = ({
  open,
  onClose,
  currentName,
  onRename,
}) => {
  const [newName, setNewName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);

  useEffect(() => {
    if (open) {
      setNewName(currentName);
      setError(null);
    }
  }, [open, currentName]);

  const handleRename = async () => {
    if (!newName.trim()) {
      setError('Template name is required');
      return;
    }

    if (newName.trim() === currentName) {
      onClose();
      return;
    }

    setIsRenaming(true);
    setError(null);

    try {
      await onRename(newName.trim());
      onClose();
    } catch (err) {
      const errorMessage = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : 'Failed to rename template';
      setError(errorMessage);
    } finally {
      setIsRenaming(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rename Template</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="Template Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            required
            fullWidth
            autoFocus
            disabled={isRenaming}
            helperText="Enter a new name for this template"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isRenaming}>
          Cancel
        </Button>
        <Button
          onClick={handleRename}
          variant="contained"
          disabled={isRenaming || !newName.trim() || newName.trim() === currentName}
        >
          {isRenaming ? 'Renaming...' : 'Rename'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateRenameDialog;

